import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  Injectable,
  Type,
} from '@angular/core';
import { LocalizationModel } from '@shared/core';
import { catchError, finalize, Observable, of, take } from 'rxjs';
import { UIBaseModal, UIConfirmModal } from '../components';

@Injectable({ providedIn: 'root' })
export class UIModalService {
  constructor(private appRef: ApplicationRef, private environmentInjector: EnvironmentInjector) {}

  create<T extends UIBaseModal>(component: Type<T>, data?: unknown) {
    const componentRef = createComponent<T>(component, {
      environmentInjector: this.environmentInjector,
    });

    // Attach the component to the change detection tree
    this.appRef.attachView(componentRef.hostView);

    // Insert the component's host element into the DOM
    document.body.appendChild(componentRef.location.nativeElement);

    // The component is now fully active and change-detected
    componentRef.instance.data = data;

    return componentRef.instance.afterCloseEmitter.asObservable().pipe(
      take(1), // Take only the first emission
      finalize(() => {
        this.destroy(componentRef);
      }),
      catchError(e => of(false)),
    );
  }

  // Method to remove/destroy component later
  destroy<T>(componentRef: ComponentRef<T>) {
    if (
      !componentRef ||
      !componentRef.hostView ||
      (componentRef.hostView as any)._view?.destroyed
    ) {
      console.warn('Component already destroyed or does not exist');
      return;
    }

    if (this.appRef.viewCount > 0) {
      this.appRef.detachView(componentRef.hostView);
    }

    if (componentRef.location?.nativeElement?.parentNode) {
      document.body.removeChild(componentRef.location.nativeElement);
    }

    componentRef.destroy();
  }

  confirm(options: IConfirmModalOptions): Observable<any> {
    const componentRef = createComponent<UIConfirmModal>(UIConfirmModal, {
      environmentInjector: this.environmentInjector,
    });

    // Attach the component to the change detection tree
    this.appRef.attachView(componentRef.hostView);

    // Insert the component's host element into the DOM
    document.body.appendChild(componentRef.location.nativeElement);

    // The component is now fully active and change-detected
    componentRef.instance.title = options?.title;
    componentRef.instance.destription = options?.desctiption;
    componentRef.instance.buttonColor = options?.buttonColor;
    componentRef.instance.buttons = options?.buttons;
    componentRef.instance.onOk = options?.onOk;

    return componentRef.instance.afterCloseEmitter.asObservable().pipe(
      take(1), // Take only the first emission
      catchError(() => of(false)),
      finalize(() => {
        this.destroy(componentRef);
      }),
    );
  }
}

export interface IConfirmModalOptions {
  title?: string | LocalizationModel;
  desctiption?: string | LocalizationModel;
  buttonColor?: 'basic' | 'accent' | 'danger';
  buttons?: IConfirmModalButtonOptions[];
  onOk?: () => Observable<any>;
}

export interface IConfirmModalButtonOptions {
  title: string | LocalizationModel;
  color?: 'basic' | 'accent' | 'danger';
  type?: 'primary' | 'secondary';
  onClicked?: () => Observable<any>;
}
